import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { ExtensionBridge } from "../components/ExtensionBridge";
import PlannerRules from "../components/PlannerRules";
import { authedFetch, postJson } from "../lib/api";
import { todayLocal } from "../lib/dates";
import { waitForPendingPlannerWrites } from "../lib/planner";
import {
  DEFAULT_PREFERENCES,
  toClock,
  toMinutes,
  type PlannerOptions,
  type PlannerPreferences,
} from "../lib/plannerPreferences";
import {
  creditsDueKey,
  creditsLabel,
  nextCreditsRecheck,
  planIdentity,
  removePastPlans,
  removePlan,
  setSeats,
  weekSummary,
  type PlannedScreening,
  type PlannerBody,
  type PlannerWeek,
  type PlannerSuggestion,
  type PlannerSuggestionsBody,
} from "../lib/plannerPage";

// The Movie Planner.
//
// THE ROUTE MUST STAY `/movie_planner`. The amc-seat-extension's `sync.js`
// gates its captured-seats write-back on `pathname.endsWith('/movie_planner')`,
// so renaming this route silently stops seats captured on AMC's seat map from
// ever reaching the sheet, with no error in either codebase.
//
// What Streamlit made hard and this fixes: each plan was a row of `st.columns`
// whose COUNT changed depending on whether the plan had an auditorium, so the
// columns did not line up between rows. Everything here is one grid.

const SEAT_SAVE_DEBOUNCE_MS = 800;
type StoredPlannerPreferences = Partial<PlannerPreferences> & { _configured?: boolean };

/** The auditorium chip, with previous visits behind a native tooltip. */
function AuditoriumChip({ plan }: { plan: PlannedScreening }) {
  const history = plan.auditorium_history;
  if (!plan.auditorium || !history) return null;
  return (
    <span
      title={history.first_time ? "First time in this auditorium" : history.summary}
      className={
        "shrink-0 rounded border px-1.5 py-0.5 text-xs " +
        (history.first_time
          ? "border-emerald-800 text-emerald-400"
          : "border-neutral-700 text-neutral-400")
      }
    >
      Aud {plan.auditorium}
      {history.first_time ? " ✨" : ""}
    </span>
  );
}

/** The seats field. Debounced, because it writes to Google Sheets per edit. */
function SeatsInput({
  plan,
  onSave,
}: {
  plan: PlannedScreening;
  onSave: (plan: PlannedScreening, seats: string) => void;
}) {
  const [value, setValue] = useState(plan.seats);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const saved = useRef(plan.seats);

  // Re-sync when the row's seats change underneath us -- a captured-seats
  // write-back from the extension lands as a refetch, not as a keystroke.
  useEffect(() => {
    setValue(plan.seats);
    saved.current = plan.seats;
  }, [plan.seats]);

  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);

  /** Cancel any pending save and write `next`, if it is actually new.
   *
   *  `saved` is a ref rather than derived from the `plan.seats` prop because a
   *  timeout scheduled in one render has to see an update written by a LATER
   *  render's resync effect -- a value captured in the closure cannot.
   */
  const flush = (next: string) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = null;
    if (next !== saved.current) {
      saved.current = next;
      onSave(plan, next);
    }
  };

  const change = (next: string) => {
    setValue(next);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => flush(next), SEAT_SAVE_DEBOUNCE_MS);
  };

  return (
    <input
      aria-label={`Seats for ${plan.movie_title}`}
      value={value}
      placeholder="Seats"
      onChange={(e) => change(e.target.value)}
      onBlur={() => flush(value)}
      className="w-24 rounded-md border border-neutral-800 bg-neutral-900 px-2 py-1 text-sm"
    />
  );
}

/** A film title that links back to the Finder's search for its full listings.
 *
 * Deliberately `/?q=` and not a `/search` route: the Movie Finder's four views
 * are component state because the Chrome extension returns early unless
 * `pathname === "/"` before writing resolved links back, so a view that lived
 * at its own path would silently disable link resolution for anyone who landed
 * there first. A query string leaves the pathname alone.
 *
 * One component rather than the markup twice: a planned screening and a
 * suggestion render the same title in two places, and the encoding is the kind
 * of detail that drifts when it is copied.
 */
function FinderLink({ title }: { title: string }) {
  return (
    <Link
      to={`/?q=${encodeURIComponent(title)}`}
      title={`See all showtimes for ${title}`}
      className="underline decoration-neutral-700 underline-offset-2 hover:decoration-neutral-400"
    >
      {title}
    </Link>
  );
}

/** One metadata pill on a suggestion row.
 *
 *  Seven copies of this markup had accumulated, each repeating the padding and
 *  text size, which is how one of them ends up a pixel off. The tones are a
 *  fixed set so two adjacent pills never share a hue -- the opening date sat in
 *  blue next to the blue "double feature" pill and the pair read as one
 *  control.
 */
const TAG_TONES = {
  blue: "border-blue-900 text-blue-300",
  purple: "border-purple-900 text-purple-300",
  cyan: "border-cyan-900 text-cyan-400",
  amber: "border-amber-800 text-amber-300",
  emerald: "border-emerald-900 text-emerald-300",
  neutral: "border-neutral-700 text-neutral-400",
} as const;

function Tag({
  tone,
  title,
  children,
}: {
  tone: keyof typeof TAG_TONES;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <span
      title={title}
      className={`shrink-0 rounded border px-1.5 py-0.5 text-xs ${TAG_TONES[tone]}`}
    >
      {children}
    </span>
  );
}

function CreditsTag({ plan }: { plan: PlannedScreening }) {
  const credits = plan.stay_for_credits;
  if (!credits) return null;
  const { text, title } = creditsLabel(credits);
  const tone =
    credits.verdict === "yes" ? "emerald" : credits.verdict === "unknown" ? "amber" : "neutral";
  return (
    <Tag tone={tone} title={title}>
      {text}
    </Tag>
  );
}

function PlanRow({
  plan,
  onSeats,
  onRemove,
  busy,
}: {
  plan: PlannedScreening;
  onSeats: (plan: PlannedScreening, seats: string) => void;
  onRemove: (plan: PlannedScreening) => void;
  busy: boolean;
}) {
  return (
    <li className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-md border border-neutral-800 bg-neutral-900/40 px-3 py-2 text-sm">
      <span className="font-medium text-neutral-200">
        {plan.day_label}, {plan.time_range}
      </span>
      <span className="text-neutral-300">
        <FinderLink title={plan.movie_title} />
      </span>
      <span className="text-neutral-500">@ {plan.theater_short}</span>
      {plan.format && (
        <span className="rounded bg-neutral-800 px-1.5 py-0.5 font-mono text-xs text-neutral-300">
          {plan.format}
        </span>
      )}
      <AuditoriumChip plan={plan} />
      {plan.price != null && (
        <span className="text-neutral-500">${plan.price.toFixed(2)}</span>
      )}
      <CreditsTag plan={plan} />
      {/* A plan for a film already logged this week is not spending a slot.
          Saying so is the difference between a total that looks wrong and one
          that explains itself. */}
      {plan.already_seen && (
        <span
          title="Already logged in Movie Tracker, so it is not using a second slot"
          className="rounded border border-neutral-700 px-1.5 py-0.5 text-xs text-neutral-400"
        >
          seen
        </span>
      )}

      <span className="ml-auto flex items-center gap-2">
        <SeatsInput plan={plan} onSave={onSeats} />
        {plan.ticket_url && (
          <a
            href={plan.ticket_url}
            target="_blank"
            rel="noreferrer"
            title="Open the AMC seat map"
            className="rounded-md border border-neutral-700 px-2 py-1 hover:border-neutral-500"
          >
            🎟️
          </a>
        )}
        {plan.has_ended && (
          <span
            title="This showtime has ended -- log it in Movie Tracker"
            className="rounded-md border border-emerald-900 px-2 py-1 text-emerald-500"
          >
            ✅
          </span>
        )}
        <button
          onClick={() => onRemove(plan)}
          disabled={busy}
          aria-label={`Remove ${plan.movie_title} from the planner`}
          className="rounded-md border border-neutral-700 px-2 py-1 hover:border-red-800 disabled:opacity-40"
        >
          ✕
        </button>
      </span>
    </li>
  );
}

/** How full a week is, as a bar rather than a coloured circle.
 *
 *  The plan table said this with 🔴🟡🟢, which is the only emoji on the page
 *  carrying meaning rather than decoration, reads at one size only, and gives a
 *  screen reader nothing. Four segments for four A-List slots is the same
 *  information in the same space, and the count beside it stays the text.
 */
function SlotMeter({ used, limit }: { used: number; limit: number }) {
  const tone =
    used >= limit ? "bg-red-500/80"
    : used === limit - 1 ? "bg-amber-500/80"
    : "bg-emerald-500/80";
  return (
    <span
      className="inline-flex items-center gap-0.5 align-middle"
      role="img"
      aria-label={`${used} of ${limit} slots used`}
    >
      {Array.from({ length: limit }, (_, index) => (
        <span
          key={index}
          className={`h-1.5 w-4 rounded-sm ${index < used ? tone : "bg-neutral-700"}`}
        />
      ))}
    </span>
  );
}

function WeekBlock({
  week,
  onSeats,
  onRemove,
  busy,
}: {
  week: PlannerWeek;
  onSeats: (plan: PlannedScreening, seats: string) => void;
  onRemove: (plan: PlannedScreening) => void;
  busy: boolean;
}) {
  const detail = week.seen
    ? `${week.seen} seen, ${week.planned} planned${
        week.already_seen ? `, ${week.already_seen} both` : ""
      }`
    : week.full
      ? "week full"
      : `${week.remaining} slot${week.remaining === 1 ? "" : "s"} remaining`;

  return (
    <section className="space-y-2">
      {/* Same shape as the suggestion weeks above: label, count, then the
          grey qualifier. The two halves of this page describe the same weeks
          and used to do it in two different formats. */}
      <h3 className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium">
        <span>{week.label}: {week.used}/{week.limit} slots used</span>
        <SlotMeter used={week.used} limit={week.limit} />
        <span className="font-normal text-xs text-neutral-500">{detail}</span>
      </h3>
      <ul className="space-y-2">
        {week.plans.map((plan) => (
          <PlanRow
            key={`${plan.date}|${plan.time}|${plan.movie_title}|${plan.theater}`}
            plan={plan}
            onSeats={onSeats}
            onRemove={onRemove}
            busy={busy}
          />
        ))}
      </ul>
    </section>
  );
}

export function Planner() {
  const [body, setBody] = useState<PlannerBody | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [params, setParams] = useSearchParams();
  const [preferences, setPreferences] = useState<PlannerPreferences>(DEFAULT_PREFERENCES);
  const [options, setOptions] = useState<PlannerOptions | null>(null);
  // Session dismissals. Deliberately component state and never persisted:
  // "not tonight" is a different statement from "never", and the permanent one
  // already has a home in the Not Interested list. Both clear on reload.
  const [skipFilms, setSkipFilms] = useState<string[]>([]);
  const [skipScreenings, setSkipScreenings] = useState<
    Array<{ date: string; time: string; movie_title: string; theater: string }>
  >([]);
  const [preferencesLoaded, setPreferencesLoaded] = useState(false);
  const [blockedDate, setBlockedDate] = useState("");
  const [blockedWindowDate, setBlockedWindowDate] = useState("");
  const [blockedFrom, setBlockedFrom] = useState("14:00");
  const [blockedTo, setBlockedTo] = useState("18:00");
  const [suggestions, setSuggestions] = useState<PlannerSuggestionsBody | null>(null);
  const [building, setBuilding] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const preferenceSaveTimer = useRef<number | null>(null);
  const preferenceSaveChain = useRef(Promise.resolve());
  const pendingPreferences = useRef<PlannerPreferences | null>(null);
  const suggestionRequestVersion = useRef(0);

  const load = useCallback(async () => {
    // A plan added from the Finder's chip is fire-and-forget there, so
    // opening this page right after can otherwise land its GET before that
    // Sheets write finishes -- and this page fetches once on mount and never
    // polls again, so the new plan would then be missing until a full manual
    // reload. Waiting for it here closes that race.
    await waitForPendingPlannerWrites();
    const res = await authedFetch("/api/planner/weeks");
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return (await res.json()) as PlannerBody;
  }, []);

  const refresh = useCallback(() => {
    load()
      .then(setBody)
      .catch((err: Error) => setError(err.message));
  }, [load]);

  // Ask for "stay for credits" lookups whenever the set of films still owed
  // one changes; refetch only if something was answered.
  const creditsInFlight = useRef(false);
  const checkCredits = useCallback(async () => {
    if (creditsInFlight.current) return;
    creditsInFlight.current = true;
    try {
      let answered = 0;
      for (let round = 0; round < 6; round++) {
        const result = await postJson<{ checked: number; remaining: number }>(
          "/api/planner/credits", {},
        );
        answered += result?.checked ?? 0;
        // The server sets failed films aside, so a round with no answers
        // still moves the queue on.
        if (!result?.remaining) break;
      }
      if (answered > 0) refresh();
    } catch {
      // Decoration, not the page: stay quiet.
    } finally {
      creditsInFlight.current = false;
    }
  }, [refresh]);

  const [creditsClock, setCreditsClock] = useState(() => Date.now());
  const creditsDue = creditsDueKey(body, creditsClock);
  useEffect(() => {
    if (creditsDue) void checkCredits();
  }, [creditsDue, checkCredits]);

  // The 30-minutes-before recheck, while the page is open. Loading the page
  // inside that window asks too, through `creditsDue` above.
  const nextRecheck = nextCreditsRecheck(body, creditsClock);
  useEffect(() => {
    if (nextRecheck === null) return;
    // 30s past the server's window opening absorbs browser/server clock skew.
    const delay = Math.min(Math.max(0, nextRecheck - Date.now()) + 30_000, 2 ** 31 - 1);
    const timer = window.setTimeout(() => setCreditsClock(Date.now()), delay);
    return () => window.clearTimeout(timer);
  }, [nextRecheck]);

  const enqueuePreferenceSave = useCallback(
    (next: PlannerPreferences, onSaved?: () => void) => {
      preferenceSaveChain.current = preferenceSaveChain.current
        .catch(() => undefined)
        .then(() => postJson<PlannerPreferences>("/api/planner/preferences", next))
        .then(() => { onSaved?.(); })
        .catch((err: Error) => {
          setError(`Could not save planner preferences: ${err.message}`);
          throw err;
        });
      return preferenceSaveChain.current;
    },
    [],
  );

  useEffect(() => {
    authedFetch("/api/planner/options")
      .then((res) => (res.ok ? (res.json() as Promise<PlannerOptions>) : null))
      .then((value) => setOptions(value))
      // The panel degrades to ids without labels rather than failing to render.
      .catch(() => setOptions(null));
  }, []);

  useEffect(() => {
    authedFetch("/api/planner/preferences")
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json() as Promise<StoredPlannerPreferences>;
      })
      .then((stored) => {
        const legacy = !stored._configured ? localStorage.getItem("amc-movie-planner-preferences") : null;
        let next = { ...DEFAULT_PREFERENCES, ...stored } as PlannerPreferences;
        if (legacy) {
          try {
            next = { ...next, ...JSON.parse(legacy) };
            void enqueuePreferenceSave(
              next,
              () => localStorage.removeItem("amc-movie-planner-preferences"),
            ).catch(() => undefined);
          } catch {
            // Ignore malformed legacy browser state. Central preferences win.
          }
        }
        setPreferences(next);
        setPreferencesLoaded(true);
      })
      .catch((err: Error) => setError(`Could not load planner preferences: ${err.message}`));
  }, [enqueuePreferenceSave]);

  useEffect(() => () => {
    if (preferenceSaveTimer.current !== null) window.clearTimeout(preferenceSaveTimer.current);
    const pending = pendingPreferences.current;
    if (pending) {
      pendingPreferences.current = null;
      void enqueuePreferenceSave(pending).catch(() => undefined);
    }
  }, [enqueuePreferenceSave]);

  const updatePreferences = (next: PlannerPreferences) => {
    setPreferences(next);
    pendingPreferences.current = next;
    if (preferenceSaveTimer.current !== null) window.clearTimeout(preferenceSaveTimer.current);
    preferenceSaveTimer.current = window.setTimeout(() => {
      preferenceSaveTimer.current = null;
      if (pendingPreferences.current === next) pendingPreferences.current = null;
      void enqueuePreferenceSave(next).catch(() => undefined);
    }, 350);
  };

  const buildSuggestions = useCallback(() => {
    if (!preferencesLoaded) return;
    const requestVersion = ++suggestionRequestVersion.current;
    if (!preferences.automatic_enabled) {
      setSuggestions(null);
      setBuilding(false);
      return;
    }
    setBuilding(true);
    setError(null);
    postJson<PlannerSuggestionsBody>("/api/planner/suggestions", {
      ...preferences,
      skip_films: skipFilms,
      skip_screenings: skipScreenings,
    })
      .then((result) => {
        if (requestVersion === suggestionRequestVersion.current) {
          setSuggestions(Array.isArray(result?.weeks) ? result : null);
        }
      })
      .catch((err: Error) => {
        if (requestVersion === suggestionRequestVersion.current) {
          setError(`Could not build the plan: ${err.message}`);
        }
      })
      .then(() => {
        if (requestVersion === suggestionRequestVersion.current) setBuilding(false);
      });
  }, [preferences, preferencesLoaded, skipFilms, skipScreenings]);

  // Suggestions are a view of the current planner state, not a separate
  // action. Build them on entry and whenever an availability or preference
  // rule changes. Debouncing keeps textarea edits to one request per pause.
  // Build once when the page opens, then only when asked. This used to
  // re-fire 350ms after EVERY preference edit, so the plan churned while the
  // rules were still being set -- and each rebuild is a full solve over the
  // horizon. `preferencesLoaded` is in the deps rather than `preferences`, so
  // editing a rule no longer triggers one.
  useEffect(() => {
    if (!preferencesLoaded) return;
    const timer = window.setTimeout(buildSuggestions, 0);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferencesLoaded]);

  const addSuggestion = useCallback((suggestion: PlannerSuggestion) => {
    const key = `${suggestion.date}|${suggestion.time}|${suggestion.movie_title}|${suggestion.theater}`;
    setAdding(key);
    postJson("/api/planner/plan", {
      date: suggestion.date,
      time: suggestion.time,
      movie_title: suggestion.movie_title,
      theater: suggestion.theater,
      format: suggestion.format,
    })
      .then(() => refresh())
      .then(() => setSuggestions((prev) => prev ? {
        ...prev,
        weeks: prev.weeks.map((week) => ({
          ...week,
          suggestions: week.suggestions.filter((item) => item !== suggestion),
          filled: Math.max(0, week.filled - 1),
          remaining: Math.min(week.limit, week.remaining + 1),
        })),
      } : prev))
      .catch((err: Error) => setError(`Could not add ${suggestion.movie_title}: ${err.message}`))
      .then(() => setAdding(null));
  }, [refresh]);

  const addAllSuggestions = useCallback(async () => {
    if (!suggestions) return;
    setAdding("__all__");
    setError(null);
    try {
      // Keep writes serial. Each plan rewrites the shared Google Sheet, so a
      // Promise.all here would race whole-sheet snapshots and lose rows.
      for (const suggestion of suggestions.weeks.flatMap((week) => week.suggestions)) {
        await postJson("/api/planner/plan", {
          date: suggestion.date,
          time: suggestion.time,
          movie_title: suggestion.movie_title,
          theater: suggestion.theater,
          format: suggestion.format,
        });
      }
      setSuggestions(null);
      refresh();
    } catch (err) {
      setError(`The automatic plan stopped partway through: ${(err as Error).message}`);
      refresh();
    } finally {
      setAdding(null);
    }
  }, [refresh, suggestions]);

  // The extension's inbound half. It marks these entries synced BEFORE
  // navigating here and never resends them, so the parameter is stripped only
  // after the POST resolves -- clearing it first would lose the batch to a
  // failed request with nothing left to retry from.
  const captured = params.get("captured_seats");
  useEffect(() => {
    if (!captured) {
      refresh();
      return;
    }
    let active = true;
    postJson<{ received: number; applied: number }>("/api/planner/captured-seats", {
      captured,
    })
      .then((result) => {
        if (!active) return;
        if (result.applied < result.received) {
          setNotice(
            `Applied ${result.applied} of ${result.received} captured seat entries.`,
          );
        }
      })
      .catch((err: Error) => {
        if (active) setError(`Captured seats did not save: ${err.message}`);
      })
      .then(() => {
        if (!active) return;
        setParams(new URLSearchParams(), { replace: true });
        refresh();
      });
    return () => {
      active = false;
    };
  }, [captured, refresh, setParams]);

  /** Apply a change locally, send it, and reconcile.
   *
   *  On failure it always refetches -- the refetch IS the rollback, so the
   *  optimistic update never has to be undone by hand.
   *
   *  `alwaysRefresh` exists for exactly one caller and is a flag rather than
   *  three hand-written variants so the difference stays visible. Clearing past
   *  plans is the only mutation whose local guess can be WRONG on success: the
   *  server decides that boundary in Pacific over the whole sheet, while the
   *  client guesses from the browser's day.
   */
  const mutate = useCallback(
    (
      apply: (prev: PlannerBody) => PlannerBody,
      send: () => Promise<unknown>,
      { alwaysRefresh = false } = {},
    ) => {
      setBody((prev) => (prev ? apply(prev) : prev));
      return send()
        .then(() => { if (alwaysRefresh) refresh(); })
        .catch((err: Error) => {
          setError(err.message);
          refresh();
        });
    },
    [refresh],
  );

  const onSeats = useCallback(
    (plan: PlannedScreening, seats: string) =>
      mutate(
        (prev) => setSeats(prev, plan, seats),
        () =>
          postJson<{ saved: boolean }>("/api/planner/seats", {
            ...planIdentity(plan),
            seats,
          }).then((res) => {
            // A 200 that stored nothing: no plan matched the identity, because
            // it was removed from another tab or the sheet was hand-edited.
            // The field would otherwise keep showing a value nothing holds.
            if (!res.saved) {
              setNotice(
                `Seats for ${plan.movie_title} did not save -- that plan is no longer in the sheet.`,
              );
              refresh();
            }
          }),
      ),
    [mutate, refresh],
  );

  const onRemove = useCallback(
    (plan: PlannedScreening) => {
      setBusy(true);
      mutate(
        (prev) => removePlan(prev, plan),
        () => postJson("/api/planner/unplan", planIdentity(plan)),
      ).then(() => setBusy(false));
    },
    [mutate],
  );

  const onClearPast = useCallback(() => {
    setBusy(true);
    mutate(
      (prev) => removePastPlans(prev, todayLocal()),
      () => postJson("/api/planner/clear-past", {}),
      { alwaysRefresh: true },
    ).then(() => setBusy(false));
  }, [mutate]);

  // Only the weeks with something to offer. A full week and a week whose slate
  // had nothing that fits both render as a heading over empty space, and
  // neither is worth a line.
  const offeredWeeks = (suggestions?.weeks ?? []).filter(
    (week) => week.suggestions.length > 0,
  );

  return (
    <div className="mx-auto max-w-3xl space-y-5 p-4 pb-16">
      <ExtensionBridge />

      <div className="flex items-center gap-3">
        <Link to="/" className="text-sm text-neutral-400 underline hover:text-neutral-200">
          ← Movie Finder
        </Link>
        <h1 className="text-xl font-semibold">📅 Movie Planner</h1>
      </div>

      {error && (
        <div role="alert" className="rounded-md border border-red-900 bg-red-950/40 p-3 text-sm">
          {error}
        </div>
      )}
      {notice && (
        <div role="status" className="rounded-md border border-amber-900 bg-amber-950/40 p-3 text-sm">
          {notice}
        </div>
      )}

      {body && !body.available && (
        <div role="alert" className="rounded-md border border-amber-900 bg-amber-950/40 p-3 text-sm">
          Google Sheets is unavailable, so the planner could not be read.
        </div>
      )}

      <section className="space-y-3 rounded-lg border border-neutral-800 bg-neutral-900/40 p-4">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="font-medium text-neutral-200">Automatic weekly plan</h2>
          </div>
          {building && <span className="shrink-0 text-xs text-neutral-500">Updating…</span>}
        </div>
        <label className="flex items-center gap-2 text-sm text-neutral-300">
          <input
            type="checkbox"
            checked={preferences.automatic_enabled}
            onChange={(e) => updatePreferences({ ...preferences, automatic_enabled: e.target.checked })}
            className="accent-emerald-400"
          />
          Enable automatic planning
        </label>
        <details className="rounded-md border border-neutral-800 bg-neutral-950/40 p-3">
          <summary className="cursor-pointer text-sm font-medium text-neutral-300">
            Planning rules
            <span className="ml-2 font-normal text-xs text-neutral-500">
              theaters, hours, ranking
            </span>
          </summary>
          <div className="mt-3 space-y-3">
        <PlannerRules
          preferences={preferences}
          options={options}
          onChange={updatePreferences}
        />
        <details className="rounded-md border border-neutral-800 bg-neutral-950/40 p-3">
          <summary className="cursor-pointer text-sm font-medium text-neutral-300">
            Specific dates
          </summary>
          <div className="mt-3 space-y-3">
            <div className="flex flex-wrap items-end gap-3 text-sm">
              <label className="space-y-1">
                <span className="block text-neutral-500">Block a date</span>
                <input
                  type="date"
                  value={blockedDate}
                  onChange={(e) => setBlockedDate(e.target.value)}
                  className="rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5"
                />
              </label>
              <button
                onClick={() => {
                  const dates = preferences.calendar.blocked_dates ?? [];
                  if (!blockedDate || dates.includes(blockedDate)) return;
                  updatePreferences({
                    ...preferences,
                    calendar: { ...preferences.calendar, blocked_dates: [...dates, blockedDate].sort() },
                  });
                  setBlockedDate("");
                }}
                className="rounded-md border border-neutral-700 px-3 py-1.5 hover:border-neutral-500"
              >
                Add blocked date
              </button>
              <label className="space-y-1">
                <span className="block text-neutral-500">Block time on date</span>
                <input type="date" value={blockedWindowDate} onChange={(e) => setBlockedWindowDate(e.target.value)} className="rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5" />
              </label>
              <label className="space-y-1">
                <span className="block text-neutral-500">From</span>
                <input type="time" value={blockedFrom} onChange={(e) => setBlockedFrom(e.target.value)} className="rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5" />
              </label>
              <label className="space-y-1">
                <span className="block text-neutral-500">To</span>
                <input type="time" value={blockedTo} onChange={(e) => setBlockedTo(e.target.value)} className="rounded-md border border-neutral-700 bg-neutral-950 px-2 py-1.5" />
              </label>
              <button
                onClick={() => {
                  const from = toMinutes(blockedFrom);
                  const to = toMinutes(blockedTo);
                  if (!blockedWindowDate || from === null || to === null || from >= to) return;
                  const existing = preferences.calendar.blocked_windows ?? {};
                  updatePreferences({
                    ...preferences,
                    calendar: {
                      ...preferences.calendar,
                      blocked_windows: {
                        ...existing,
                        [blockedWindowDate]: [...(existing[blockedWindowDate] ?? []), [from, to] as [number, number]],
                      },
                    },
                  });
                  setBlockedWindowDate("");
                }}
                className="rounded-md border border-neutral-700 px-3 py-1.5 hover:border-neutral-500"
              >
                Block time
              </button>
            </div>
            {((preferences.calendar.blocked_dates ?? []).length > 0 ||
              Object.keys(preferences.calendar.blocked_windows ?? {}).length > 0) && (
              <div className="flex flex-wrap gap-2 text-xs text-neutral-400">
                {(preferences.calendar.blocked_dates ?? []).map((date) => (
                  <button
                    key={date}
                    title={`Unblock ${date}`}
                    onClick={() => updatePreferences({
                      ...preferences,
                      calendar: {
                        ...preferences.calendar,
                        blocked_dates: (preferences.calendar.blocked_dates ?? []).filter((item) => item !== date),
                      },
                    })}
                    className="rounded border border-neutral-700 px-2 py-1 hover:border-red-800"
                  >
                    {date} \u00d7
                  </button>
                ))}
                {Object.entries(preferences.calendar.blocked_windows ?? {}).flatMap(([date, windows]) => windows.map(([from, to]) => (
                  <button
                    key={`${date}-${from}-${to}`}
                    title={`Unblock ${date} ${from}-${to}`}
                    onClick={() => updatePreferences({
                      ...preferences,
                      calendar: {
                        ...preferences.calendar,
                        blocked_windows: {
                          ...preferences.calendar.blocked_windows,
                          [date]: windows.filter(([start, end]) => start !== from || end !== to),
                        },
                      },
                    })}
                    className="rounded border border-neutral-700 px-2 py-1 hover:border-red-800"
                  >
                    {date} {toClock(from)}\u2013{toClock(to)} \u00d7
                  </button>
                )))}
              </div>
            )}
          </div>
        </details>
          </div>
        </details>
      </section>

      {preferences.automatic_enabled && suggestions && (
        <section className="space-y-3 rounded-lg border border-emerald-900/60 bg-emerald-950/10 p-4">
          <div>
            <h2 className="font-medium text-neutral-200">Suggested plan</h2>
            <p className="text-sm text-neutral-400">
              Matched against your watch history and watchlist, then scheduled against your
              rules. Times shown are <strong>start times</strong>.
            </p>
          </div>
          {suggestions.taste_ranked === false && (
            <p className="rounded border border-amber-900/60 bg-amber-950/20 px-3 py-2 text-xs text-amber-300">
              Taste ranking was unavailable, so these were picked from every eligible screening
              rather than the ones matching your history. Films on your watchlist are still
              marked and still ranked first.
            </p>
          )}
          {(skipFilms.length > 0 || skipScreenings.length > 0) && (
            <p className="w-full text-xs text-neutral-500">
              Skipping {skipFilms.length > 0 && `${skipFilms.length} film${skipFilms.length === 1 ? "" : "s"}`}
              {skipFilms.length > 0 && skipScreenings.length > 0 && " and "}
              {skipScreenings.length > 0 && `${skipScreenings.length} showing${skipScreenings.length === 1 ? "" : "s"}`}
              {" "}for this session.{" "}
              <button
                onClick={() => { setSkipFilms([]); setSkipScreenings([]); }}
                className="underline hover:text-neutral-300"
              >
                Undo all
              </button>
            </p>
          )}
          <button
            onClick={buildSuggestions}
            disabled={building}
            className="rounded-md border border-neutral-700 bg-neutral-800 px-3 py-1.5 text-xs text-neutral-200 hover:border-neutral-500 hover:bg-neutral-700 disabled:opacity-40"
          >
            {building ? "Rebuilding…" : "Rebuild plan"}
          </button>
          {offeredWeeks.length > 0 && (
            <button
              onClick={addAllSuggestions}
              disabled={adding !== null}
              /* One step down from the near-white emerald it was: it is the
                 loudest thing on the page and it is a BULK action, and it also
                 has to read as the same family as the per-row Add. */
              className="rounded-md border border-emerald-700 bg-emerald-800/80 px-3 py-1.5 text-xs font-medium text-emerald-100 hover:bg-emerald-700/80 disabled:opacity-40"
            >
              {adding === "__all__" ? "Adding plan…" : "Add all suggestions"}
            </button>
          )}
          {/* A week with nothing to offer is a heading and a blank space --
              whether it is full or simply had nothing that fit, which look the
              same from here. The server still sends every horizon week, so the
              payload stays complete for anything reading it directly; this is a
              rendering choice, and the "nothing to plan" message below covers
              the case where that leaves none. */}
          {offeredWeeks.length === 0 && (
            <p className="text-sm text-neutral-500">
              {suggestions.reason || "No eligible open slots found for the current availability rules."}
            </p>
          )}
          {offeredWeeks.map((week) => (
            <div key={week.week_key} className="space-y-2">
              <h3 className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm font-medium">
                <span>
                  {week.label || week.week_key}: {week.filled}/{week.limit} slots used
                </span>
                <SlotMeter used={week.filled} limit={week.limit} />
                {weekSummary(week) && (
                  <span className="font-normal text-xs text-neutral-500">
                    {weekSummary(week)}
                  </span>
                )}
              </h3>
              {week.suggestions.map((suggestion) => {
                const key = `${suggestion.date}|${suggestion.time}|${suggestion.movie_title}|${suggestion.theater}`;
                return (
                  <div key={key} className="rounded-md border border-neutral-800 bg-neutral-900/40 px-3 py-2 text-sm">
                    {/* Three bands, one job each: what the screening IS, what
                        the server decided about it, and the controls.

                        Two rejected arrangements, both tried 2026-08-13. Tags
                        running on beside the title wraps mid-identity, so the
                        theater ends up alone under a row of pills. Tags sharing
                        the controls' band leaves them on the LAST line, where
                        they read as part of the button group. Their own band
                        under the title avoids both.

                        Everything shared one wrapping row before that, which
                        failed differently again: a variable number of tags
                        pushed the buttons somewhere different on every row.
                        The buttons are filled and the tags are not, which is
                        the cheapest way to say which one is clickable. */}
                    <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                      <span className="font-medium">
                        {suggestion.day_label}, {suggestion.time_range}
                      </span>
                      <span>
                        <FinderLink title={suggestion.movie_title} />
                      </span>
                      <span className="text-neutral-500">@ {suggestion.theater}</span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
                      {suggestion.taste_basis === "watchlist" && (
                        <Tag tone="emerald">★ watchlist</Tag>
                      )}
                      {suggestion.double_feature && <Tag tone="blue">double feature</Tag>}
                      {suggestion.only_option && (
                        <Tag
                          tone="amber"
                          title="The only showing of this film that fits your rules anywhere in the horizon"
                        >
                          only option
                        </Tag>
                      )}
                      {/* No "leaving soon" counterpart, and there will not be
                          one: a film's last listed date is how far ahead AMC
                          put it on sale, not when its run ends. Measured
                          2026-08-13, 16 of the 23 films already playing shared
                          two adjacent last-dates -- Toy Story 5 and Moana among
                          them. This one survives because `run_dates` is
                          withheld entirely for a film that has not opened, so
                          the same publishing window cannot make an unopened
                          film look like a one-off. See
                          `planner_solver.film_run`. */}
                      {suggestion.limited_engagement && (
                        <Tag
                          tone="purple"
                          title="Fewer than a week of dates anywhere in the horizon: a repertory run or a one-off event"
                        >
                          limited engagement
                        </Tag>
                      )}
                      {/* Empty means the run date is UNKNOWN -- the common case
                          for anything already running when the registry's
                          column shipped -- so this renders nothing rather than
                          a placeholder. The server says "Opening day" rather
                          than repeating the date the row already shows. */}
                      {suggestion.opening_label && (
                        <Tag
                          tone="cyan"
                          title="When this film's run started, from the nightly first-seen registry"
                        >
                          {suggestion.opening_label}
                        </Tag>
                      )}
                      {/* Never shown alongside the run date -- the server makes
                          the two exclusive. */}
                      {suggestion.rerelease_label && (
                        <Tag
                          tone="neutral"
                          title="Not this film's first run: it came out before last year"
                        >
                          {suggestion.rerelease_label}
                        </Tag>
                      )}
                      {suggestion.taste_reason && suggestion.taste_basis !== "watchlist" && (
                        <Tag tone="neutral">{suggestion.taste_reason}</Tag>
                      )}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-2">
                      <span className="text-xs text-neutral-500">{suggestion.reason}</span>
                      {/* `ml-auto` on the group, so the controls sit bottom
                          right whether the reason is one clause or three. */}
                      <span className="ml-auto flex shrink-0 items-center gap-1.5">
                        <button
                          onClick={() => {
                            setSkipScreenings((current) => [...current, {
                              date: suggestion.date,
                              time: suggestion.time,
                              movie_title: suggestion.movie_title,
                              theater: suggestion.theater,
                            }]);
                          }}
                          title="Drop this showing for now and keep the film in play"
                          className="rounded-md border border-neutral-700 bg-neutral-800 px-2 py-1 text-xs text-neutral-200 hover:border-neutral-500 hover:bg-neutral-700"
                        >
                          Another time
                        </button>
                        <button
                          onClick={() =>
                            setSkipFilms((current) =>
                              current.includes(suggestion.movie_title)
                                ? current
                                : [...current, suggestion.movie_title])
                          }
                          title="Skip this film for this session only; nothing is saved"
                          className="rounded-md border border-neutral-700 bg-neutral-800 px-2 py-1 text-xs text-neutral-200 hover:border-red-800 hover:bg-neutral-700"
                        >
                          Not this film
                        </button>
                        <button
                          onClick={() => addSuggestion(suggestion)}
                          disabled={adding === key}
                          className="rounded-md border border-emerald-800 bg-emerald-900/60 px-2.5 py-1 text-xs font-medium text-emerald-200 hover:border-emerald-600 hover:bg-emerald-900 disabled:opacity-40"
                        >
                          {adding === key ? "Adding…" : "Add"}
                        </button>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </section>
      )}

      {!preferences.automatic_enabled && (
        <p className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-4 text-sm text-neutral-500">
          Automatic planning is paused. Your existing manual plans are unchanged.
        </p>
      )}

      {body?.available && body.total === 0 && (
        <p className="rounded-lg border border-neutral-800 bg-neutral-900/50 p-4 text-sm text-neutral-400">
          Nothing planned yet. Add a showtime from the{" "}
          <Link to="/" className="underline hover:text-neutral-200">
            Movie Finder
          </Link>
          .
        </p>
      )}

      {body?.available && body.total > 0 && (
        /* A titled card, like "Suggested plan" above. This half is the actual
           plan and had no heading at all -- it began with a bare "5 planned"
           and a button, so the more important of the two sections read as the
           less important one. */
        <section className="space-y-3 rounded-lg border border-neutral-800 bg-neutral-900/30 p-4">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-medium text-neutral-200">
              Your plan{" "}
              <span className="text-sm font-normal text-neutral-500">
                {body.total} booked
              </span>
            </h2>
            <button
              onClick={onClearPast}
              disabled={busy}
              className="rounded-md border border-neutral-700 bg-neutral-800 px-3 py-1.5 text-xs text-neutral-200 hover:border-neutral-500 hover:bg-neutral-700 disabled:opacity-40"
            >
              Clear past plans
            </button>
          </div>

          {body.weeks.map((week) => (
            <WeekBlock
              key={week.week_key}
              week={week}
              onSeats={onSeats}
              onRemove={onRemove}
              busy={busy}
            />
          ))}
        </section>
      )}

      {!body && !error && <p className="text-sm text-neutral-500">Loading…</p>}

      <p className="text-xs text-neutral-600">
        AMC A-List allows {body?.limit ?? 4} movies per week (Friday through Thursday).
      </p>
    </div>
  );
}
